import { api } from '../client';

import type { StockDocumentMutationOptions } from './documentMutationCache';

export type SalesInvoiceKind = 'invoice' | 'correction';
export type SalesInvoiceSource = 'erp' | 'external_ksef';
export type SalesInvoiceStatus = 'draft' | 'issued';
export type SalesInvoiceKsefStatus =
  | 'not_sent'
  | 'submitting'
  | 'pending'
  | 'error'
  | 'rejected'
  | 'accepted'
  | 'reconcile_required'
  | 'not_required';
export type SalesInvoicePaymentState = 'unpaid' | 'partial' | 'paid' | 'unknown' | 'not_required';
export type SalesInvoicePaymentUiState =
  | 'awaiting'
  | 'overdue'
  | 'paid'
  | 'unknown'
  | 'not_required';

/** Order-level invoice state (MD-0091): derived from the order's invoices. */
export type B2BSaleInvoiceState =
  | 'not_required'
  | 'to_invoice'
  | 'draft'
  | 'sending'
  | 'reconcile'
  | 'rejected'
  | 'issued';

/** An external KSeF invoice marked "not a sale" (MD-0091): kept visible, left out of revenue. */
export interface SalesInvoiceExclusion {
  reason: string;
  excluded_at: string;
  excluded_by: string | null;
  excluded_by_name?: string | null;
}

export interface SalesInvoiceSummary {
  id: string;
  kind: SalesInvoiceKind;
  source: SalesInvoiceSource;
  status: SalesInvoiceStatus;
  number: string | null;
  seller_company_key: string | null;
  order_id: string | null;
  order_sale_no: string | null;
  customer_id: string | null;
  buyer_name: string | null;
  buyer_nip: string | null;
  issue_date: string | null;
  sale_date: string | null;
  payment_due_date: string | null;
  net_total: string | null;
  vat_total: string | null;
  gross_total: string | null;
  outside_ksef: boolean;
  ksef_status: SalesInvoiceKsefStatus;
  ksef_number: string | null;
  corrects_invoice_id: string | null;
  corrects_number: string | null;
  exclusion: SalesInvoiceExclusion | null;
  updated_at: string;
}

export interface SalesInvoiceListItem extends SalesInvoiceSummary {
  customer_name: string | null;
  payment_state: SalesInvoicePaymentState;
  payment_paid_total: string | null;
  payment_outstanding_total: string | null;
  needs_review?: boolean;
}

export interface SalesInvoiceParty {
  nip: string | null;
  name: string | null;
  address: string | null;
}

export interface SalesInvoiceSeller extends SalesInvoiceParty {
  key?: string | null;
}

export interface SalesInvoiceLine {
  id: string;
  line_no: number;
  product_id: string | null;
  name: string;
  unit: string | null;
  unit_code: string | null;
  quantity: string;
  unit_price: string;
  net_amount: string;
  vat_rate: string;
  vat_amount: string;
  gross_amount: string;
  correction_state: 'before' | 'after' | null;
}

export type SalesInvoiceSubmissionStatus =
  | 'submitting'
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'error'
  | 'reconcile_required'
  | 'released'
  | (string & {});

export interface SalesInvoiceSubmission {
  id: string;
  attempt_no: number;
  status: SalesInvoiceSubmissionStatus;
  invoice_number: string | null;
  issue_date: string | null;
  ksef_number: string | null;
  status_code: number | null;
  status_description: string | null;
  status_details: string[] | null;
  upo_download_url: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface SalesInvoicePaymentSummary {
  state: SalesInvoicePaymentState;
  ui_state: SalesInvoicePaymentUiState;
  due_total: string | null;
  paid_total: string;
  outstanding_total: string | null;
  /** Credit left after corrections (MD-0092), null when none. */
  credit_total: string | null;
  /** Bank/cash date of the settling money when paid. */
  paid_on: string | null;
  urgency_flag?: string;
}

export interface SalesInvoiceActions {
  can_edit: boolean;
  can_send: boolean;
  can_reconcile: boolean;
  can_correct: boolean;
  can_pay: boolean;
  can_offset_credit: boolean;
  can_delete: boolean;
  can_link_order: boolean;
  can_exclude: boolean;
  can_include: boolean;
}

export interface SalesInvoiceCorrectionInfo {
  reason: string | null;
  type: '1' | '2' | '3' | null;
  buyer_before: SalesInvoiceParty | null;
  buyer_after: SalesInvoiceParty | null;
}

export interface SalesInvoiceDetail extends SalesInvoiceSummary {
  seller: SalesInvoiceSeller | null;
  buyer: SalesInvoiceParty | null;
  delivery_address: string | null;
  payment_method: string | null;
  show_payment: boolean;
  bank_account_key: string | null;
  bank_account_number: string | null;
  notes: string | null;
  correction: SalesInvoiceCorrectionInfo | null;
  lines: SalesInvoiceLine[];
  /** True while a draft shows prospective lines taken from its order (not yet issued). */
  lines_preview: boolean;
  /** Prospective totals of a draft computed from `lines`. */
  preview_totals: { net_total: string; vat_total: string; gross_total: string } | null;
  submissions: SalesInvoiceSubmission[];
  payment: SalesInvoicePaymentSummary | null;
  /** Money-ledger rows (MD-0092): received payments/credit and credit given by this invoice's corrections. */
  allocations: SalesInvoiceAllocation[];
  actions: SalesInvoiceActions;
}

export type SalesInvoiceAllocationKind = 'payment' | 'credit_offset' | 'refund' | 'void';

export interface SalesInvoiceAllocation {
  id: string;
  kind: SalesInvoiceAllocationKind;
  /** `given` = credit this invoice's corrections gave to another invoice. */
  direction: 'received' | 'given';
  amount: string;
  effective_date: string | null;
  receipt_id: string | null;
  receipt_channel: 'bank' | 'cash' | null;
  receipt_booking_date: string | null;
  receipt_counterparty: string | null;
  receipt_manual: boolean | null;
  /** Why the receipt has no bank row (cash, outside_bank, supplier_declared, historical) */
  receipt_manual_source?: string | null;
  forwarded_from_booking_date: string | null;
  sales_invoice_id: string | null;
  sales_invoice_number: string | null;
  credit_source_invoice_id: string | null;
  credit_source_number: string | null;
  void_of_id: string | null;
  voided_by_id: string | null;
  is_active: boolean;
  note: string | null;
  void_reason: string | null;
  created_at: string | null;
  created_by: string | null;
  created_via: 'web_admin' | 'agent' | 'migration' | (string & {});
}

export interface SalesInvoiceDetailResponse {
  request_id: string;
  invoice: SalesInvoiceDetail;
}

export type SalesInvoiceMutationResponse = SalesInvoiceDetailResponse;

export interface SalesInvoiceListResponse {
  request_id?: string;
  items: SalesInvoiceListItem[];
  total: number;
}

export interface SalesInvoiceFilters {
  [key: string]: string | number | boolean | string[] | undefined;
  q?: string;
  customer_id?: string[];
  company_key?: string;
  status?: SalesInvoiceStatus;
  ksef_status?: SalesInvoiceKsefStatus;
  kind?: SalesInvoiceKind;
  source?: SalesInvoiceSource;
  needs_review?: boolean;
  excluded?: boolean;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface SalesInvoiceSettingsRequest {
  seller_company_key?: string | null;
  bank_account_key?: string | null;
  issue_date?: string | null;
  sale_date?: string | null;
  payment_method?: string | null;
  payment_due_date?: string | null;
  show_payment?: boolean;
  notes?: string | null;
}

export interface SalesInvoiceKsefXmlPreviewResponse {
  request_id: string;
  invoice_id: string;
  environment: string;
  preview_ksef_number: string;
  /** False while the FV/FK number is only a preview; it is reserved when the invoice is sent (MD-0090). */
  invoice_number_assigned?: boolean;
  xml_content: string;
  parsed_preview: Record<string, unknown>;
}

export type CreateSalesInvoiceCorrectionItemRequest =
  | {
      original_line_id: string;
      corrected_quantity: string;
      corrected_unit_price: string;
      vat_rate?: string | null;
    }
  | {
      product_id: string;
      unit_code: string;
      corrected_quantity: string;
      corrected_unit_price: string;
      vat_rate?: string | null;
    };

export interface CreateSalesInvoiceCorrectionRequest {
  reason: string;
  correction_type?: '1' | '2' | '3' | null;
  items: CreateSalesInvoiceCorrectionItemRequest[];
  corrected_buyer_name?: string | null;
  corrected_buyer_address?: string | null;
  corrected_buyer_nip?: string | null;
}

/** Exactly the reference field of the chosen source is sent (MD-0092). */
export type CreateSalesInvoiceAllocationRequest =
  | { source: 'receipt'; amount: string; receipt_id: string; note?: string }
  | { source: 'cash'; amount: string; paid_on: string; note?: string }
  | { source: 'credit'; amount: string; credit_source_invoice_id: string; note?: string };

export interface VoidSalesInvoiceAllocationRequest {
  reason: string;
}

export interface SalesInvoiceAllocationMutationResponse {
  request_id: string;
  allocation: SalesInvoiceAllocation;
  invoice: SalesInvoiceDetail;
}

export interface DeleteSalesInvoiceResponse {
  request_id?: string;
  deleted?: boolean;
}

export interface IngestExternalSalesInvoicesResponse {
  request_id: string;
  [key: string]: unknown;
}

type MutationOptions = Pick<StockDocumentMutationOptions, 'idempotencyKey' | 'expectedUpdatedAt'>;

/** Idempotency key plus optimistic concurrency via the shared `X-Expected-Updated-At` header. */
function mutationOptions(options?: MutationOptions): {
  idempotencyKey?: string;
  headers?: Record<string, string>;
} {
  const requestOptions: { idempotencyKey?: string; headers?: Record<string, string> } = {};
  if (options?.idempotencyKey) requestOptions.idempotencyKey = options.idempotencyKey;
  const expectedUpdatedAt = options?.expectedUpdatedAt?.trim();
  if (expectedUpdatedAt) requestOptions.headers = { 'X-Expected-Updated-At': expectedUpdatedAt };
  return requestOptions;
}

const BASE = '/stock/sales-invoices';

export const salesInvoicesApi = {
  list: (filters: SalesInvoiceFilters = {}): Promise<SalesInvoiceListResponse> =>
    api.webAdmin.get<SalesInvoiceListResponse>(BASE, { params: filters }),

  detail: (id: string): Promise<SalesInvoiceDetailResponse> =>
    api.webAdmin.get<SalesInvoiceDetailResponse>(`${BASE}/${id}`),

  update: (
    id: string,
    data: SalesInvoiceSettingsRequest,
    options?: MutationOptions,
  ): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.patch<SalesInvoiceMutationResponse>(
      `${BASE}/${id}`,
      data,
      mutationOptions(options),
    ),

  deleteDraft: (id: string, options?: MutationOptions): Promise<DeleteSalesInvoiceResponse> =>
    api.webAdmin.delete<DeleteSalesInvoiceResponse>(`${BASE}/${id}`, mutationOptions(options)),

  ksefXmlPreview: (id: string): Promise<SalesInvoiceKsefXmlPreviewResponse> =>
    api.webAdmin.get<SalesInvoiceKsefXmlPreviewResponse>(`${BASE}/${id}/ksef/xml-preview`),

  sendToKsef: (id: string, options?: MutationOptions): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.post<SalesInvoiceMutationResponse>(
      `${BASE}/${id}/ksef/send`,
      undefined,
      mutationOptions(options),
    ),

  reconcileKsef: (id: string, options?: MutationOptions): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.post<SalesInvoiceMutationResponse>(
      `${BASE}/${id}/ksef/reconcile`,
      undefined,
      mutationOptions(options),
    ),

  pdf: (id: string) => api.webAdmin.blob(`${BASE}/${id}/pdf`),

  createCorrection: (
    id: string,
    data: CreateSalesInvoiceCorrectionRequest,
    options?: MutationOptions,
  ): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.post<SalesInvoiceMutationResponse>(
      `${BASE}/${id}/corrections`,
      data,
      mutationOptions(options),
    ),

  createAllocation: (
    id: string,
    data: CreateSalesInvoiceAllocationRequest,
    options?: MutationOptions,
  ): Promise<SalesInvoiceAllocationMutationResponse> =>
    api.webAdmin.post<SalesInvoiceAllocationMutationResponse>(
      `${BASE}/${id}/allocations`,
      data,
      mutationOptions(options),
    ),

  voidAllocation: (
    id: string,
    allocationId: string,
    data: VoidSalesInvoiceAllocationRequest,
    options?: MutationOptions,
  ): Promise<SalesInvoiceAllocationMutationResponse> =>
    api.webAdmin.post<SalesInvoiceAllocationMutationResponse>(
      `${BASE}/${id}/allocations/${allocationId}/void`,
      data,
      mutationOptions(options),
    ),

  linkOrder: (
    id: string,
    orderId: string,
    options?: MutationOptions,
  ): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.post<SalesInvoiceMutationResponse>(
      `${BASE}/${id}/link-order`,
      { order_id: orderId },
      mutationOptions(options),
    ),

  exclude: (
    id: string,
    reason: string,
    options?: MutationOptions,
  ): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.post<SalesInvoiceMutationResponse>(
      `${BASE}/${id}/exclude`,
      { reason },
      mutationOptions(options),
    ),

  include: (id: string, options?: MutationOptions): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.post<SalesInvoiceMutationResponse>(
      `${BASE}/${id}/include`,
      undefined,
      mutationOptions(options),
    ),

  ingestExternal: (options?: MutationOptions): Promise<IngestExternalSalesInvoicesResponse> =>
    api.webAdmin.post<IngestExternalSalesInvoicesResponse>(
      `${BASE}/external/ingest`,
      undefined,
      mutationOptions(options),
    ),
};
